<script setup lang="ts">
import { computed, inject } from 'vue';

import type { UnitStatusEnum } from '@/enums/UnitStatus.ts';
import type { Unit } from '@/typings/units/Unit';
import { DefaultUnitsFilters, type UnitsFilters } from '@/typings/units/UnitsFilters';

import UnitsBedroomsCountFilter from './filters/UnitsBedroomsCountFilter.vue';
import UnitsFloorSelectorFilter from './filters/UnitsFloorSelectorFilter.vue';
import UnitsLivingAreaRangeFilter from './filters/UnitsLivingAreaRangeFilter.vue';
import UnitsPriceRangeFilter from './filters/UnitsPriceRangeFilter.vue';
import UnitsStatusFilter from './filters/UnitsStatusFilter.vue';
import UnitsAppliedFilters from './UnitsAppliedFilters.vue';

const filterCount = inject<number>('filterCount', 0);

const filters = defineModel<UnitsFilters>('filters', { default: { ...DefaultUnitsFilters } });

const props = defineProps<{
  allUnits: Unit[];
}>();

const bedroomsCountOptions = computed(() => {
  const uniqueBedroomsCountOptions = new Set<number>();

  if (!props.allUnits || !props.allUnits.length) {
    return [];
  }

  props.allUnits.forEach((unit) => {
    if (typeof unit.bedrooms === 'number') {
      uniqueBedroomsCountOptions.add(unit.bedrooms);
    }
  });

  return Array.from(uniqueBedroomsCountOptions).sort((a, b) => a - b);
});

const floorOptions = computed(() => {
  const uniqueFloorOptions = new Set<number>();

  if (!props.allUnits || !props.allUnits.length) {
    return [];
  }

  props.allUnits.forEach((unit) => {
    if (typeof unit.floorNo === 'number') {
      uniqueFloorOptions.add(unit.floorNo);
    }
  });

  return Array.from(uniqueFloorOptions).sort((a, b) => a - b);
});

const statusOptions = computed(() => {
  const uniqueStatusOptions = new Set<UnitStatusEnum>();

  if (!props.allUnits || !props.allUnits.length) {
    return [];
  }

  props.allUnits.forEach((unit) => {
    if (typeof unit.status === 'string') {
      uniqueStatusOptions.add(unit.status);
    }
  });

  return Array.from(uniqueStatusOptions).sort((a, b) => a.localeCompare(b));
});

const minPriceRangeValue = computed(() => {
  const prices = props.allUnits
    .map((unit: Unit) => unit.basePrice)
    .filter((price): price is number => price !== null);
  
  if (prices.length === 0) return 0;
  
  const minPrice = Math.min(...prices);
  return minPrice > 0 ? minPrice : 0;
});

const maxPriceRangeValue = computed(() => {
  const prices = props.allUnits
    .map((unit: Unit) => unit.basePrice)
    .filter((price): price is number => price !== null);
  
  if (prices.length === 0) return 0;
  
  const maxPrice = Math.max(...prices);
  return maxPrice > 0 ? maxPrice : 0;
});

const minLivingAreaRangeValue = computed(() => {
  const minLivingArea = Math.min(...props.allUnits.map((unit: Unit) => unit.livingArea ?? 0));
  return minLivingArea > 0 ? minLivingArea : 0;
});

const maxLivingAreaRangeValue = computed(() => {
  const maxLivingArea = Math.max(...props.allUnits.map((unit: Unit) => unit.livingArea ?? 0));
  return maxLivingArea > minLivingAreaRangeValue.value ? maxLivingArea : minLivingAreaRangeValue.value;
});
</script>

<template>
  <div class="filtersPanel filtersPanel_units">
    <UnitsAppliedFilters v-model:filters="filters"
                         :allow-filter-deletion="true"
                         class="filtersPanel-appliedFilters" />
    
    <hr v-if="filterCount > 0"
        class="separator">

    <div class="filtersPanel-filters">
      <template v-if="floorOptions.length > 0">
        <UnitsFloorSelectorFilter v-model="filters.selectedFloorNo"
                                  :options="floorOptions"
                                  class="filtersPanel-fieldset" />
      </template>
                                
      <template v-if="maxPriceRangeValue > 0">
        <hr class="separator" />
        <UnitsPriceRangeFilter v-model="filters.selectedPriceRange"
                               :min-value="minPriceRangeValue"
                               :max-value="maxPriceRangeValue"
                               class="filtersPanel-fieldset" />
      </template>
                              
      <template v-if="statusOptions.length > 0">
        <hr class="separator" />
        <UnitsStatusFilter v-model="filters.selectedStatuses"
                           :options="statusOptions"
                           class="filtersPanel-fieldset" />
      </template>
                          
      <template v-if="maxLivingAreaRangeValue > minLivingAreaRangeValue">
        <hr class="separator" />
        <UnitsLivingAreaRangeFilter v-model="filters.selectedLivingAreaRange"
                                    :min-value="minLivingAreaRangeValue"
                                    :max-value="maxLivingAreaRangeValue"
                                    class="filtersPanel-fieldset" />
      </template>
    </div>
  </div>
</template>

<style lang="scss">
.filtersPanel {
  --flowSpacing: var(--spc48-24);
  --buttonGap: 8px;

  > *:not(:empty) + * {
    margin-top: 1.6rem;
  }

  &-filters {
    > * + * {
      margin-top: 1.6rem;
    }
  }

  &-fieldset {
    padding: 0;
    border: none;

    legend {
      font-size: 1.2em;
      font-weight: 600;
    }

    > * + * {
      margin-top: 8px;
    }
  }
}
</style>
